import { useEffect, useRef, useState } from 'react';
import { FC } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { blobToFile, getImageExtension, sentenceCase, showToast } from '../../../utils/miscFunctions';
import ImageCropper from '../imageCropper';
import { ReactComponent as ImageIcon } from './upload.svg';
import { ScrollToFormikError } from '../ScrollToFormikError';
import { Camera, Download, Upload, User } from 'lucide-react';
import CSV from './csv.png';
import PDF from './pdf-document.jpg';
import Button from '../../form-control/Button';



const FileUpload: FC<{
  title?: string;
  allowSelect?: boolean;
  multiple?: boolean;
  maxFiles?: number;
  onFileLoad: (images: File[] | ArrayBuffer | null) => void;
  defaultImage?: any;
  type?: 'image' | 'document' | 'audio' | 'video' | 'csv';
  label?: string;
  label2?: string;
  imageAlt?: string;
  id?: string;
  required?: boolean;
  error?: string;
  description?: string;
  cropImage?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  imageContainerWidth?: string; //Set this in css unit
  imageContainerHeight?: string; //Set this in css unit
  currentImage?: string;
  onFileDeleted?: (file: File[] | ArrayBuffer | null) => void;
  onProceed?: () => void;
  showResetAndSubmitButtons?: boolean;
  showFileMax?: boolean;
  onFileSrcChange?: (fileUrl: string) => void;
  profilePhoto?: boolean;
  savedImage?: any;
  isProfile?: boolean;
  subtitle?: string;
}> = ({
  allowSelect = true,
  multiple = false,
  maxFiles,
  onFileLoad,
  onProceed,
  onFileDeleted,
  showResetAndSubmitButtons = false,
  defaultImage,
  type,
  title,
  label,
  label2,
  imageAlt,
  id,
  required = false,
  error,
  description,
  cropImage = true,
  maxWidth = 600,
  maxHeight = 200,
  imageContainerHeight = '177px',
  imageContainerWidth = '100%',
  showFileMax = true,
  onFileSrcChange,
  profilePhoto,
  savedImage,
  isProfile = false,
  subtitle
}) => {
    const [currentImage, setCurrentImage] = useState<any>(savedImage);
    const [currentImageFile, setCurrentImageFile] = useState<File | null>();
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<{ file: any; name: string; size: string; id: string }[]>(
      []
    );
    const [fileType, setFileType] = useState<string>('image/x-png,image/jpeg, .pdf');
    const [hovered, setHovered] = useState<boolean>(false);
    const [isCropperOpen, setIsCropperOpen] = useState<boolean>(false);

    const fieldRef = useRef<HTMLDivElement>(null);

    maxWidth = profilePhoto ? 300 : maxWidth;
    maxHeight = profilePhoto ? 300 : maxHeight;
    imageContainerHeight = profilePhoto ? '170px' : imageContainerHeight;
    imageContainerWidth = profilePhoto ? '170px' : imageContainerWidth;
    let imageInput: HTMLInputElement | null = null;
    const fileSize = 10485760;

    useEffect(() => {
      if (defaultImage) {
        setImagePreviews([
          {
            file: null,
            name: defaultImage['docUrl'],
            size: '',
            id: `default-${Date.now()}`
          }
        ]);
      }
    }, [defaultImage]);

    useEffect(() => {
      onFileSrcChange?.(currentImage);
    }, [currentImage]);

    function onImageUpload(e: any) {
      const files: any = Array.from(e.target.files);
      if (files.length === 0) {
        return;
      }

      // Validate all files
      const oversizedFiles = files.filter((file: File) => file.size > fileSize);
      if (oversizedFiles.length > 0) {
        showToast.error(`${oversizedFiles.length} file(s) exceed 10MB limit`);
        return;
      }

      if (multiple && maxFiles && uploadedFiles.length + files.length > maxFiles) {
        showToast.error(`You can upload a maximum of ${maxFiles} files`);
        return;
      }

      // Process all files
      const processedFiles: File[] = [];
      const newPreviews: { file: any; name: string; size: string; id: string }[] = [];

      files.forEach((file: File, index: number) => {
        const reader = new FileReader();
        
        reader.onloadend = () => {
          const file_size =
            file.size > 1024
              ? file.size > 1048576
                ? `${Math.round(file.size / 1048576)}mb`
                : `${Math.round(file.size / 1024)}kb`
              : `${file.size}b`;

          const preview = {
            file: reader.result,
            name: file.name,
            size: file_size,
            id: `${file.name}-${Date.now()}-${index}`
          };

          processedFiles.push(file);
          
          if (multiple) {
            setImagePreviews(prev => [...prev, preview]);
            setUploadedFiles(prev => [...prev, file]);
          } else {
            setImagePreviews([preview]);
            setUploadedFiles([file]);
            setCurrentImage(reader.result);
            setCurrentImageFile(file);

            if (cropImage && type === 'image') {
              openImageCropper(reader.result);
            }
          }

          // Call onFileLoad when all files are processed
          if (processedFiles.length === files.length) {
            onFileLoad(processedFiles);
          }
        };

        reader.readAsDataURL(file);
      });
    }

    useEffect(() => {
      if (type) {
        switch (type) {
          case 'image':
            setFileType('image/x-png,image/jpeg');
            break;
          case 'document':
            setFileType('.pdf');
            break;
          case 'csv':
            setFileType('.csv');
            break;
        }
      }
    }, [type]);

    const handleImageCrop = (crop: any) => {
      setCurrentImageFile(crop.file);
      setCurrentImage(crop.url);
      setIsCropperOpen(false);
      onFileLoad([blobToFile(crop.file, currentImageFile)]);
    };

    const removeFile = (fileId: string) => {
      const newPreviews = imagePreviews.filter(preview => preview.id !== fileId);
      const newFiles = uploadedFiles.filter((_, index) => imagePreviews[index]?.id !== fileId);
      
      setImagePreviews(newPreviews);
      setUploadedFiles(newFiles);
      
      if (newFiles.length === 0) {
        setCurrentImage(null);
        setCurrentImageFile(null);
      }
      
      onFileDeleted?.(newFiles);
      onFileLoad(newFiles);
    };

    function openImageCropper(url: any) {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        if (img.height < maxHeight || img.width < maxWidth) {
          setCurrentImage(null);
          setCurrentImageFile(null);
          showToast.error(`Minimum required size for image is ${maxWidth}x${maxHeight}.`);
        } else {
          setIsCropperOpen(true);
        }
      };
    }

    return (
      <div className={clsx('', profilePhoto ? 'w-[170px]' : ' w-full')} ref={fieldRef}>
        {/* File upload section */}
        <article aria-label="File Upload" className="relative h-full flex flex-col">
          {/* Label */}
          {label && (
            <label
              htmlFor={id}
              className={clsx('block text-lg font-medium text-black text-center dark:text-darkBg-mainText', {
                ['text-danger-main']: error && required
              })}>
              {label} <span className="text-danger-main">{required && '*'}</span>
              {/* Description */}
              {description && (
                <div
                  className={clsx('text-xs text-gray-400 italic dark:text-gray-500', {
                    ['text-danger-border']: error && required
                  })}>
                  {description}
                </div>
              )}
            </label>
          )}
          <style>
            {`
          @keyframes float {
            0%   { transform: translateY(0); }
            50%  { transform: translateY(-10px); }
            100% { transform: translateY(0); }
          }
        `}
          </style>
          {/* Scroll area */}
          <section className={clsx('space-y-8 ', profilePhoto ? 'rounded-full ' : 'overflow-auto w-full flex flex-col ')}>
            <div className={clsx(
              'w-full flex flex-col items-center justify-center space-y-4 md:space-y-2 ',
              profilePhoto ? 'h-44' : 'min-h-44'
            )}
              style={{ width: imageContainerWidth, height: imageContainerHeight }}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}>

              {/* Multiple file previews */}
              {multiple && imagePreviews.length > 0 ? (
                <div className="w-full h-full overflow-y-auto">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-2">
                    {imagePreviews.map((preview, index) => (
                      <div key={preview.id} className="relative group">
                        <div className="border border-gray-300 dark:border-darkBg-border rounded-lg overflow-hidden h-24 flex items-center justify-center bg-gray-50 dark:bg-darkBg">
                          {fileType === ".csv" ? (
                            <img src={CSV} className='h-12 w-12 object-contain' />
                          ) : fileType === ".pdf" || uploadedFiles[index]?.type === "application/pdf" ? (
                            <img src={PDF} className='h-12 w-12 object-contain' />
                          ) : (
                            <img
                              src={preview.file}
                              alt={preview.name}
                              className="h-full w-full object-cover"
                            />
                          )}
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                          {preview.name}
                        </div>
                        <button
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeFile(preview.id)}
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {imagePreviews.length > 0 && (
                    <div className="text-center mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {imagePreviews.length} file(s) uploaded
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Single file display or upload area */}
                  {currentImage || (!multiple && imagePreviews.length > 0) ? (
                    <div className={clsx(fileType === ".csv" || fileType === ".pdf" || fileType === "image/x-png,image/jpeg, .pdf" && 'border-none flex items-center justify-center', 'border-[1px] border-gray-300 dark:border-darkBg-border overflow-hidden ', profilePhoto && 'rounded-full')}>
                      {/* Trash icon */}
                      {hovered && (
                        <div className={clsx('absolute bottom-0 z-10 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-primary-main rounded-full p-1 outline-none size-8 cursor-pointer',
                          profilePhoto ? 'translate-y-3' : 'translate-y-1'
                        )}
                          onClick={() => {
                            setCurrentImage(null)
                            setCurrentImageFile(null)
                            setImagePreviews([])
                            setUploadedFiles([])
                            onFileDeleted?.(null)
                          }}>
                          <TrashIcon className="w-full text-gray-500 " />
                        </div>
                      )}
                      {/* Uploaded image */}
                      {fileType === ".csv" ?
                        <img src={CSV} className='w-[50%] rounded-b-xl mx-auto' style={{
                          animation: 'float 1.5s ease-in-out infinite',
                          boxShadow: '0 8px 12px rgba(0, 0, 0, 0.3)',
                        }} /> : fileType === ".pdf" || currentImageFile?.type === "application/pdf" ?
                          <img src={PDF} className='h-[100%] rounded-b-xl' style={{
                            animation: 'float 1.5s ease-in-out infinite',
                            boxShadow: '0 8px 12px rgba(0, 0, 0, 0.3)',
                          }} />
                          : <img
                            style={{ objectFit: 'cover', backgroundPosition: 'center' }}
                            src={currentImage || imagePreviews[0]?.file}
                            className={clsx('', profilePhoto ? ' h-[170px] w-[170px]' : 'w-full')}
                            alt={imageAlt}
                          />}
                    </div>
                  ) : (
                    // Placeholder for image upload
                    <>
                      {/* File input */}
                      <input
                        type="file"
                        id="imageFile"
                        style={{ display: 'none' }}
                        accept={fileType}
                        ref={(input) => {
                          imageInput = input;
                        }}
                        onChange={onImageUpload}
                        className="hidden"
                        multiple={multiple}
                      />
                      {/* Image upload container */}
                      {allowSelect && (
                        <>
                          {!isProfile &&
                            <div
                              className={clsx(
                                'w-full h-full  border-2  flex flex-col justify-center items-center cursor-pointer space-y-4 md:space-y-2',
                                profilePhoto
                                  ? 'border-black rounded-full'
                                  : 'border-[1px] border-dashed border-[#8CEB8C] bg-[#8CEB8C]/[0.1]'
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                imageInput && (imageInput.value = ''); //reset so same image can be picked - no valid justification for this, but it seems useful

                                imageInput?.click();
                              }}>
                              {/* Image icon */}
                              <span className="block rounded-[50%] p-2 bg-[#8CEB8C]">
                                <ImageIcon className="w-[30px] h-[30px]" />
                              </span>
                              {/* Image upload text */}
                              <p className="text-dark text-sm text-center dark:text-darkBg-mainText">{title}</p>
                              {/* File max size information */}
                              {showFileMax && (
                                <p className="text-[#8D5241] text-xs md:text-sm text-center dark:text-gray-400">
                                  Max 10MB, {sentenceCase(fileType)}
                                </p>
                              )}
                            </div>
                          }
                          {isProfile && <div className="flex w-full items-center space-x-6">
                            <div className="relative">
                              <div className="w-24 h-24 bg-gray-200 dark:bg-darkBg-border rounded-full flex items-center justify-center">
                                <User className="h-12 w-12 text-gray-500 dark:text-gray-400" />
                              </div>
                              <button className="absolute bottom-0 right-0 w-8 h-8 bg-gray-800 dark:bg-darkBg-mainText rounded-full flex items-center justify-center text-white hover:bg-gray-700 dark:hover:bg-gray-600">
                                <Camera className="h-4 w-4" />
                              </button>
                            </div>
                            <div>
                              <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText whitespace-nowrap">{title}</h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400 sm:whitespace-nowrap">{subtitle || "Upload a professional photo for your profile"}</p>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  imageInput && (imageInput.value = ''); //reset so same image can be picked - no valid justification for this, but it seems useful

                                  imageInput?.click();
                                }}
                                className="mt-2 whitespace-nowrap inline-flex items-center px-3 py-2 border border-gray-300 dark:border-darkBg-border shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-darkBg hover:bg-gray-50 dark:hover:bg-darkBg-border">
                                <Upload className="mr-2 h-4 w-4" />
                                {title}
                              </button>
                            </div>
                          </div>}
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Add more files button for multiple mode */}
              {multiple && imagePreviews.length > 0 && (!maxFiles || imagePreviews.length < maxFiles) && (
                <div className="mt-4 text-center">
                  <input
                    type="file"
                    id="additionalFiles"
                    style={{ display: 'none' }}
                    accept={fileType}
                    ref={(input) => {
                      imageInput = input;
                    }}
                    onChange={onImageUpload}
                    className="hidden"
                    multiple={multiple}
                  />
                  <button
                    onClick={() => imageInput?.click()}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add More Files
                  </button>
                </div>
              )}
            </div>
          </section>

        </article>
        {label2 && (
          <label
            htmlFor={id}
            className={clsx('flex py-3  items-center justify-center text-xs font-medium text-gray-500 dark:text-gray-400 text-center', {
              ['text-danger-main']: error && required
            })}>
            <div className='cursor-pointer border-b border-primary-main flex hover:text-black dark:hover:text-darkBg-mainText transition-all duration-200'>{label2} <Download className='h-5 w-5 ' /></div>

          </label>
        )}
        {/* Image cropper component */}
        <ImageCropper
          aspect={maxWidth / maxHeight}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          horizontal-cover
          sourceImage={currentImage}
          isOpen={isCropperOpen}
          onCropCancel={() => {
            setCurrentImageFile(null);
            setCurrentImage(null);
            setIsCropperOpen(false);
          }}
          onCropFinished={handleImageCrop}
          imageFormat={getImageExtension(currentImageFile)}
        />
        {/* Scroll to Formik error component */}
        <ScrollToFormikError fieldName={id ?? ''} fieldRef={fieldRef} />

        {showResetAndSubmitButtons &&
          <div className="w-full flex gap-4 mt-6 !mb-4">
            <Button
              type="button"
              label="Reset"
              onClick={() => {
                setCurrentImageFile(null)
                setCurrentImage(null);
                setIsCropperOpen(false);
              }}
              bgColor='secondary'
              fullWidth

            />
            <Button
              label="Import"
              onClick={onProceed}
              bgColor='primary'
              fullWidth
              disabled={currentImageFile ? false : true}
            // loading={submitting}
            // disabled={submitting}
            />
          </div>}
      </div>
    );
  };

export default FileUpload;
